import { MaterialIcons } from '@expo/vector-icons';
import { DrawerActions } from '@react-navigation/native';
import { Stack, useLocalSearchParams, useNavigation, usePathname, useRouter } from 'expo-router';
import React, { lazy, useCallback, useMemo } from 'react';
import { ActivityIndicator, FlatList, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import PollingStationItem from '@/components/PollingStationItem';
import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import { IconSymbol } from '@/components/ui/IconSymbol';
import AppStyles from '@/constants/Styles';
import { useBackgroundMyActiveEngagements, useBackgroundPollingStations } from '@/hooks/useBackgroundFetch';
import { useThemeColor } from '@/hooks/useThemeColor';
import { useNavigationStore } from '@/stores/navigationStore';
import { PollingStation } from '@/types/polling_station';

// Lazy load the map component for better performance
const CrossPlatformMapView = lazy(() => import('@/components/MapView'));

export default function TellingBattlePlanDetailsScreen() {
  const { battlePlanId } = useLocalSearchParams<{ battlePlanId: string }>();
  const [tab, setTab] = React.useState<'list' | 'map'>('list');
  const {getSelectedBranch} = useNavigationStore();
  const selectedBranch = getSelectedBranch();
  const insets = useSafeAreaInsets();
  
  // Theme-aware colors for tabs
  const borderColor = useThemeColor({}, 'border' as any);
  const cardBackground = useThemeColor({}, 'cardBackground' as any);
  const tintColor = useThemeColor({}, 'tint');
  const iconColor = useThemeColor({}, 'icon');
  
  // Use background fetch hooks for polling stations
  const { data: pollingStations, loading: pollingStationsLoading, error: pollingStationsError } = useBackgroundPollingStations(battlePlanId);
  const { data: myEngagements, loading: myEngagementsLoading, error: myEngagementsError } = useBackgroundMyActiveEngagements();
  
  // Determine overall loading and error states
  const loading = pollingStationsLoading || myEngagementsLoading;
  const error = pollingStationsError || myEngagementsError;

  const router = useRouter();
  const navigation = useNavigation();
  const pathname = usePathname();


  // Polling stations don't need geometry loading as they have lat/long coordinates
  // We'll use markers instead of polygons on the map

  // Handle polling station selection - navigate to the polling station detail screen
  const handlePollingStationSelect = useCallback((pollingStation: PollingStation) => {
    router.push({
      pathname: './[battlePlanId]/[pollingStationId]',
      params: { 
        battlePlanId: battlePlanId,
        pollingStationId: pollingStation.polling_station_id.toString()
      }
    });
  }, [router, battlePlanId]);

  // Memoized map region calculation based on polling stations
  const mapRegion = useMemo(() => {
    if (!pollingStations || pollingStations.length === 0) return null;
    
    // Calculate bounds from polling station coordinates
    let minLat = Infinity, maxLat = -Infinity, minLon = Infinity, maxLon = -Infinity;
    
    pollingStations.forEach((station: PollingStation) => {
      if (station.latitude && station.longitude) {
        minLat = Math.min(minLat, station.latitude);
        maxLat = Math.max(maxLat, station.latitude);
        minLon = Math.min(minLon, station.longitude);
        maxLon = Math.max(maxLon, station.longitude);
      }
    });
    
    if (minLat === Infinity) return null;
    
    const centerLat = (minLat + maxLat) / 2;
    const centerLon = (minLon + maxLon) / 2;
    const latDelta = (maxLat - minLat) * 1.5; // Add some padding
    const lonDelta = (maxLon - minLon) * 1.5;
    
    // Ensure the zoom level is close enough to show markers (MARKER_VISIBILITY_THRESHOLD = 0.05)
    // Use a smaller delta to ensure markers are visible
    const minDelta = 0.03; // Slightly smaller than the threshold to ensure visibility
    
    return {
      latitude: centerLat,
      longitude: centerLon,
      latitudeDelta: Math.max(latDelta, minDelta),
      longitudeDelta: Math.max(lonDelta, minDelta),
    };
  }, [pollingStations]);

  // Handle map marker press
  const handleMarkerPress = useCallback((marker: any) => {
    const pollingStation = pollingStations?.find((ps: PollingStation) => 
      ps.polling_station_id.toString() === marker.id
    );
    if (pollingStation) {
      handlePollingStationSelect(pollingStation);
    }
  }, [pollingStations, handlePollingStationSelect]);

  // Pre-filter polling stations based on user's active engagements
  const myBattlePlanPollingStations = useMemo(() => {
    if (!pollingStations || !myEngagements) return [];
    
    // Get the set of my engagement IDs for this battle plan
    const myEngagementIds = new Set(
      myEngagements
        .filter((eng) => eng.battle_plan_id === battlePlanId)
        .map((eng) => eng.engagement_id)
    );
    
    // Filter polling stations that contain at least one of my engagements
    const filtered = pollingStations.filter((station: PollingStation) => 
      station.polling_station_districts && station.polling_station_districts.some((eng) => myEngagementIds.has(eng.engagement_id))
    );
    
    // Sort by station name
    return filtered.sort((a: PollingStation, b: PollingStation) => 
      a.station_name.localeCompare(b.station_name)
    );
  }, [pollingStations, myEngagements, battlePlanId]);

  const otherPollingStations = useMemo(() => {
    if (!pollingStations || !myEngagements) return [];
    
    // Get the set of my engagement IDs for this battle plan
    const myEngagementIds = new Set(
      myEngagements
        .filter((eng) => eng.battle_plan_id === battlePlanId)
        .map((eng) => eng.engagement_id)
    );
    
    // Filter polling stations that don't contain any of my engagements
    const filtered = pollingStations.filter((station: PollingStation) => 
      !station.polling_station_districts || !station.polling_station_districts.some((eng) => myEngagementIds.has(eng.engagement_id))
    );
    
    // Sort by station name
    return filtered.sort((a: PollingStation, b: PollingStation) => 
      a.station_name.localeCompare(b.station_name)
    );
  }, [pollingStations, myEngagements, battlePlanId]);

  // Create combined data for FlatList with section headers
  const listData = useMemo(() => {
    const data: { type: 'header' | 'pollingStation', title?: string, pollingStation?: PollingStation }[] = [];
    
    // Add my polling stations section (only for this battle plan)
    if (myBattlePlanPollingStations.length > 0) {
      data.push({ type: 'header', title: 'My Polling Stations' });
      myBattlePlanPollingStations.forEach((pollingStation: PollingStation) => {
        data.push({ type: 'pollingStation', pollingStation });
      });
    }
    
    // Add other polling stations section
    if (otherPollingStations.length > 0) {
      data.push({ type: 'header', title: 'Other Polling Stations' });
      otherPollingStations.forEach((pollingStation: PollingStation) => {
        data.push({ type: 'pollingStation', pollingStation });
      });
    }
    
    return data;
  }, [myBattlePlanPollingStations, otherPollingStations]);

  const renderPollingStation = useCallback(({ item: pollingStation }: { item: PollingStation }) => {
    return (
      <PollingStationItem
        pollingStation={pollingStation}
        onPress={() => handlePollingStationSelect(pollingStation)}
      />
    );
  }, [handlePollingStationSelect]);

  const renderListItem = useCallback(({ item }: { item: { type: 'header' | 'pollingStation', title?: string, pollingStation?: PollingStation } }) => {
    if (item.type === 'header') {
      return (
        <View style={{ paddingHorizontal: 16, paddingVertical: 12, backgroundColor: cardBackground }}>
          <ThemedText style={AppStyles.headerText}>{item.title}</ThemedText>
        </View>
      );
    } else if (item.type === 'pollingStation' && item.pollingStation) {
      return renderPollingStation({ item: item.pollingStation });
    }
    return null;
  }, [renderPollingStation, cardBackground]);

  // Memoize keyExtractor for better FlatList performance
  const keyExtractor = useCallback((item: { type: 'header' | 'pollingStation', title?: string, pollingStation?: PollingStation }) => {
    return item.type === 'header' ? `header-${item.title}` : `pollingStation-${item.pollingStation?.polling_station_id}`;
  }, []);

  // Show loading state
  if (loading) {
    return (
      <ThemedView style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size="large" color={iconColor} />
        <ThemedText style={{ marginTop: 16 }}>Loading polling stations...</ThemedText>
      </ThemedView>
    );
  }

  // Show error state
  if (error) {
    return (
      <ThemedView style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
        <ThemedText style={AppStyles.errorText}>
          Error loading polling stations: {error.message}
        </ThemedText>
      </ThemedView>
    );
  }

  return (
    <ThemedView style={{ flex: 1 }}>
      <Stack.Screen
        options={{
          headerTitle: () => (
            <View style={AppStyles.headerContainer}>
              <ThemedText style={AppStyles.headerText}>Telling Stations</ThemedText>
            </View>
          ),
          headerLeft: () => (
            <TouchableOpacity onPress={() => navigation.dispatch(DrawerActions.toggleDrawer())} style={{ paddingLeft: 15 }}>
              <IconSymbol name="chevron.right" size={28} color={tintColor} />
            </TouchableOpacity>
          ),
          headerRight: () => (
            <TouchableOpacity onPress={() => router.push({ 
              pathname: '/(main)/[branch]/share', 
              params: { 
                branch: selectedBranch?.branch_short_name || "", 
                ref: pathname,
                battlePlanId: battlePlanId
              } 
            })} style={{ paddingRight: 15 }}>
              <IconSymbol name="square.and.arrow.up" size={24} color={tintColor} />
            </TouchableOpacity>
          ),
          headerBackVisible: false,
        }}
      />
      
      {/* Main Content Area */}
      <View style={{ flex: 1 }}>
        {/* Tab Content */}
        {tab === 'list' ? (
          <FlatList
            data={listData}
            keyExtractor={keyExtractor}
            renderItem={renderListItem}
            contentContainerStyle={{ paddingBottom: insets.bottom }}
            ListEmptyComponent={
              <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
                <ThemedText>No polling stations found for this battle plan.</ThemedText>
              </View>
            }
          />
        ) : (
          mapRegion && pollingStations ? (
            <CrossPlatformMapView
              initialRegion={mapRegion}
              polygons={[]}

              pollingStationMarkers={pollingStations.map((station: PollingStation) => ({
                id: `polling-station-${station.polling_station_id}`,
                coordinate: {
                  latitude: station.latitude,
                  longitude: station.longitude,
                },
                address: station.address,
                postcode: station.postcode,
              }))}
              onMarkerPress={handleMarkerPress}
              showUserLocation={true}
            />
          ) : (
            <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
              <ThemedText>No polling stations to display on map</ThemedText>
            </View>
          )
        )}
      </View>

      {/* Bottom Tab Navigation */}
      <View style={{
        flexDirection: 'row',
        backgroundColor: cardBackground,
        borderTopWidth: 1,
        borderTopColor: borderColor,
        paddingBottom: insets.bottom,
      }}>
        <TouchableOpacity
          style={[
            {
              flex: 1,
              paddingVertical: 12,
              alignItems: 'center',
              justifyContent: 'center',
            },
            tab === 'list' && { backgroundColor: cardBackground }
          ]}
          onPress={() => setTab('list')}
        >
          <MaterialIcons 
            name="list" 
            size={24} 
            color={tab === 'list' ? tintColor : iconColor} 
          />
          <ThemedText style={[
            { fontSize: 12, marginTop: 4 },
            tab === 'list' ? { color: tintColor } : { color: iconColor }
          ]}>List</ThemedText>
        </TouchableOpacity>
        <TouchableOpacity
          style={[
            {
              flex: 1,
              paddingVertical: 12,
              alignItems: 'center',
              justifyContent: 'center',
            },
            tab === 'map' && { backgroundColor: cardBackground }
          ]}
          onPress={() => setTab('map')}
        >
          <MaterialIcons 
            name="map" 
            size={24} 
            color={tab === 'map' ? tintColor : iconColor} 
          />
          <ThemedText style={[
            { fontSize: 12, marginTop: 4 },
            tab === 'map' ? { color: tintColor } : { color: iconColor }
          ]}>Map</ThemedText>
        </TouchableOpacity>
      </View>
    </ThemedView>
  );
}
